import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ClockIcon, 
  ShieldCheckIcon, 
  GlobeAltIcon, 
  DocumentTextIcon, 
  CurrencyDollarIcon, 
  ChartBarIcon 
} from '@heroicons/react/24/outline';

const WhyChooseSection = () => {
  const features = [
    {
      icon: ClockIcon,
      title: "Proven track record",
      description: "Operating since 2018 with consistent performance"
    },
    {
      icon: ShieldCheckIcon,
      title: "24/7 client care",
      description: "Round-the-clock support for all your needs"
    },
    {
      icon: GlobeAltIcon,
      title: "Global footprint",
      description: "Serving clients worldwide with local expertise"
    },
    {
      icon: DocumentTextIcon,
      title: "Comprehensive compliance",
      description: "Full regulatory compliance across jurisdictions"
    },
    {
      icon: CurrencyDollarIcon,
      title: "Strong financial health",
      description: "Robust financial position and security measures"
    },
    {
      icon: ChartBarIcon,
      title: "Deep liquidity",
      description: "Access to deep liquidity pools for optimal trading"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: [0.25, 0.46, 0.45, 0.94],
            delay: 0.2 
          }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-12 lg:mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6"
          >
            Why choose Segway?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-lg text-gray-600 max-w-3xl mx-auto mb-8"
          >
            Our commitment to security, innovation, and client satisfaction has made us a trusted partner in comprehensive wealth management. We combine cutting-edge technology with proven financial expertise and personalized advisory services to deliver exceptional results.
          </motion.p>
          <Link to="/apply-now">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-teal-600 font-semibold hover:text-teal-700 transition-colors duration-200"
            >
              Read more
            </motion.button>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 1, 
                ease: [0.25, 0.46, 0.45, 0.94],
                delay: index * 0.1 
              }}
              viewport={{ once: true, margin: "-50px" }}
              whileHover={{ 
                y: -4, 
                scale: 1.02,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              className="group"
            >
              <motion.div 
                className="bg-white rounded-xl p-6 lg:p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-500 ease-out"
                whileHover={{ 
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  borderColor: "#e5e7eb"
                }}
              >
                {/* Icon */}
                <motion.div 
                  className="w-12 h-12 lg:w-14 lg:h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gray-200 transition-all duration-300"
                  whileHover={{ 
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <feature.icon className="w-6 h-6 lg:w-7 lg:h-7 text-gray-600" />
                </motion.div>

                {/* Title */}
                <motion.h3 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 + 0.4 }}
                  viewport={{ once: true }}
                  className="text-lg lg:text-xl font-bold text-gray-900 mb-2"
                >
                  {feature.title}
                </motion.h3>

                {/* Description */}
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 + 0.6 }}
                  viewport={{ once: true }}
                  className="text-gray-600 text-sm lg:text-base"
                >
                  {feature.description}
                </motion.p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection; 